import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Phone, Clock, ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal } from "../components/Reveal";
import { OFFICES } from "../lib/site";

const mapEmbed = (addr) => `https://www.google.com/maps?q=${encodeURIComponent(addr)}&output=embed`;
const mapLink = (addr) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;

export default function Locations() {
  const [active, setActive] = useState(OFFICES[0].id);
  const office = OFFICES.find((o) => o.id === active);

  return (
    <div data-testid="locations-page" className="pt-[72px]">
      <section className="mx-auto max-w-[1500px] px-6 md:px-10 pt-20 md:pt-28 pb-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
        <div className="lg:col-span-8">
          <p className="overline text-flame mb-6">Locations</p>
          <h1 className="font-display uppercase tracking-tighter leading-[0.85] text-[12vw] lg:text-[7.5vw]">
            <MaskedLines lines={["Six offices.", <span key="2" className="text-cobalt">Two states.</span>]} delay={0.2} />
          </h1>
        </div>
        <div className="lg:col-span-4">
          <p className="text-muted-foreground text-lg">Serving the Inland Empire, Greater Los Angeles and Central New Jersey with local recruitment, payroll and PEO expertise.</p>
        </div>
      </section>

      <section className="mx-auto max-w-[1500px] px-6 md:px-10 pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* office list */}
        <div className="lg:col-span-6 border-t border-border">
          {OFFICES.map((o, i) => (
            <button
              key={o.id}
              data-testid={`location-${o.id}`}
              onMouseEnter={() => setActive(o.id)}
              onClick={() => setActive(o.id)}
              onFocus={() => setActive(o.id)}
              className={`group w-full text-left flex items-center justify-between border-b border-border py-6 transition-colors duration-300 ${active === o.id ? "px-4 bg-secondary" : ""}`}
            >
              <div className="flex items-baseline gap-5">
                <span className="font-mono text-xs text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                <span className={`font-display uppercase tracking-tight text-3xl md:text-5xl transition-colors ${active === o.id ? "text-cobalt" : ""}`}>{o.city}</span>
              </div>
              <span className="overline text-muted-foreground text-right">{o.state}<br className="sm:hidden" /> · {o.tag}</span>
            </button>
          ))}
        </div>

        {/* active office detail + map */}
        <div className="lg:col-span-6">
          <div className="sticky top-28">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4 }}
              >
                <div className="relative w-full h-[300px] overflow-hidden border border-border bg-secondary">
                  <iframe
                    title={`Map of ${office.label}`}
                    data-testid={`map-${office.id}`}
                    src={mapEmbed(office.address)}
                    className="w-full h-full grayscale-[0.2]"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>

                <div className="mt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="font-display uppercase tracking-tight text-3xl">{office.city}</span>
                    {office.tag === "Corporate HQ" && <span className="overline text-flame border border-flame/40 px-2 py-1">HQ</span>}
                  </div>
                  <p className="text-muted-foreground mb-6">{office.blurb}</p>

                  <ul className="space-y-3 border-t border-border pt-5">
                    <li className="flex items-start gap-3">
                      <MapPin size={17} className="text-cobalt mt-0.5 shrink-0" />
                      <a href={mapLink(office.address)} target="_blank" rel="noreferrer" data-testid={`directions-${office.id}`} className="text-sm hover:text-flame transition-colors">{office.address}</a>
                    </li>
                    <li className="flex items-center gap-3">
                      <Phone size={17} className="text-cobalt shrink-0" />
                      <a href={`tel:${office.phone.replace(/[^0-9]/g, "")}`} data-testid={`phone-${office.id}`} className="text-sm hover:text-flame transition-colors">{office.phone}</a>
                    </li>
                    <li className="flex items-center gap-3">
                      <Clock size={17} className="text-cobalt shrink-0" />
                      <span className="text-sm text-muted-foreground">{office.hours}</span>
                    </li>
                  </ul>

                  <a href={mapLink(office.address)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-6 bg-cobalt text-white px-6 py-3 rounded-full overline hover:bg-flame transition-colors duration-300">
                    Get directions <ArrowUpRight size={14} />
                  </a>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* full directory strip */}
      <section className="bg-ink text-bone">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 py-20">
          <p className="overline text-flame mb-10">All locations</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10">
            {OFFICES.map((o, i) => (
              <Reveal key={o.id} delay={(i % 3) * 0.06}>
                <div className="border-t border-bone/20 pt-5">
                  <div className="font-display uppercase tracking-tight text-2xl mb-2">{o.city}, {o.state}</div>
                  <p className="text-bone/60 text-sm">{o.address}</p>
                  <a href={`tel:${o.phone.replace(/[^0-9]/g, "")}`} className="text-flame text-sm block mt-2 hover:text-white transition-colors">{o.phone}</a>
                  <p className="text-bone/40 overline mt-2">{o.hours}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
